import { memo, useCallback, type ReactElement } from 'react';
import classnames from 'classnames';

import { pluralize } from 'shared/functions-ts';
import { WithChildren, WithError } from '@eva/types/dto';

import type { ControlDescriptor, GetFormFunction, GroupedTableClassNames, GroupRowState } from '../types';

import Controls from '../Controls';
import CollapseButton from '../CollapseButton';

import './Group.scss';

export interface GroupProps<G> {
  group: G;
  groups: G[];
  groupState: GroupRowState;
  classNames: GroupedTableClassNames;
  controls?: ControlDescriptor<G, G>[];
  getGroupId: (group: G) => number;
  getGroupForm: GetFormFunction<G, GroupRowState>;
  getGroupLabel: (group: G) => ReactElement | string;
  getGroupError?: (error: string, group: G) => ReactElement | string;
  onExpand: (group: G) => void;
  onCollapse: (group: G) => void;
  updateGroupState: (group: G, updates: Partial<GroupRowState>) => void;
}

const Group = <I, G extends WithError & WithChildren<I>>({
  group,
  groups,
  controls,
  classNames,
  groupState,
  getGroupId,
  getGroupForm,
  getGroupLabel,
  getGroupError,
  onExpand,
  onCollapse,
  updateGroupState,
}: GroupProps<G>) => {
  const groupId = getGroupId(group);

  const onUpdateRowState = useCallback(
    (updates: Partial<GroupRowState>) => {
      if (typeof updateGroupState === 'function') {
        updateGroupState(group, updates);
      }
    },
    [group, updateGroupState],
  );

  const onShowGroupForm = useCallback(() => {
    if (!groupId) {
      return;
    }

    onUpdateRowState({
      inlineForm:
        typeof getGroupForm === 'function'
          ? getGroupForm(
              group,
              {
                updateRowState: onUpdateRowState,
              },
              groupState,
            )
          : null,
    });
  }, [group, groupId, groupState, getGroupForm, onUpdateRowState]);

  const onClickCollapseButton = useCallback(
    (expanded: boolean) => {
      if (expanded) {
        onExpand(group);
        return;
      }
      onCollapse(group);
    },
    [group, onExpand, onCollapse],
  );

  if (groupState == null) {
    return;
  }

  const errorElement =
    groupState?.error &&
    (typeof getGroupError === 'function' ? (
      getGroupError(groupState.error, group)
    ) : (
      <div className="text-danger small padding-top">{groupState.error}</div>
    ));

  const messageElement = !!groupState.message && <div className="text-danger small">{groupState.message}</div>;

  let formOrTitle = null;

  if (groupState?.inlineForm) {
    formOrTitle = (
      <div className={classNames.groupInlineFormClassName}>
        <div>{groupState.inlineForm}</div>
        {errorElement}
      </div>
    );
  } else {
    const role = typeof getGroupForm === 'function' && groupId != null ? 'button' : 'presentation';

    const groupTitleClassNames = classnames([classNames.groupTitleClassName]);

    formOrTitle = (
      <h5 role={role} className={groupTitleClassNames} onClick={onShowGroupForm}>
        {getGroupLabel(group)}
      </h5>
    );
  }

  const groupClassNames = classnames({
    [classNames.groupClassName]: true,
  });

  const groupContentClassNames = classnames({
    [classNames.groupContentClassName]: true,
  });

  const groupControlsClassNames = classnames([classNames.groupControlsClassName, 'pull-right']);

  let regularForm = null;

  if (groupState.regularForm) {
    const newItemFormClassNames = classnames(['padding', classNames.regularFormClassName]);
    regularForm = <div className={newItemFormClassNames}>{groupState.regularForm}</div>;
  }

  const counterBadge =
    groupState?.isExpanded === false && group?.children?.length ? (
      <span className="badge margin-left bg-primary">
        {`${group.children.length} ${pluralize(group?.children?.length, 'item')}`}
      </span>
    ) : null;

  return (
    <div className={groupClassNames}>
      {controls?.length && (
        <div className={groupControlsClassNames}>
          <Controls
            entity={group}
            groups={groups}
            controls={controls}
            rowState={groupState}
            classNames={classNames}
            getEntityId={getGroupId}
            updateRowState={onUpdateRowState}
          />
        </div>
      )}
      <div className={groupContentClassNames}>
        {onExpand && onCollapse && (
          <CollapseButton isExpanded={groupState?.isExpanded} onClick={onClickCollapseButton} />
        )}
        {formOrTitle}
        {counterBadge}
      </div>
      {messageElement}

      {errorElement}

      {regularForm}

      <div className="clearfix"></div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(Group);
